/**
 * Routing list state machine - replaces useRoutingStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { RoutingRule, ProcessingQueue, AgentSkill } from '../types'

export interface RoutingListContext {
  rules: RoutingRule[]
  queues: ProcessingQueue[]
  agentSkills: AgentSkill[]
}

export type RoutingListEvent =
  | { type: 'SET_RULES'; rules: RoutingRule[] }
  | { type: 'ADD_RULE'; rule: RoutingRule }
  | { type: 'UPDATE_RULE'; id: string; updates: Partial<RoutingRule> }
  | { type: 'DELETE_RULE'; id: string }
  | { type: 'SET_QUEUES'; queues: ProcessingQueue[] }
  | { type: 'ADD_QUEUE'; queue: ProcessingQueue }
  | { type: 'UPDATE_QUEUE'; id: string; updates: Partial<ProcessingQueue> }
  | { type: 'DELETE_QUEUE'; id: string }
  | { type: 'INCREMENT_QUEUE_LOAD'; id: string }
  | { type: 'DECREMENT_QUEUE_LOAD'; id: string }
  | { type: 'SET_AGENT_SKILLS'; skills: AgentSkill[] }
  | { type: 'UPDATE_AGENT_SKILLS'; agentId: string; skills: Partial<AgentSkill> }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as RoutingListContext,
    events: {} as RoutingListEvent,
  },
})

export const routingListMachine = machineSetup.createMachine({
  id: 'routingList',
  context: {
    rules: [],
    queues: [],
    agentSkills: [],
  },
  on: {
    SET_RULES: {
      actions: assign({
        rules: ({ event }) => (event.type === 'SET_RULES' ? event.rules : []),
      }),
    },
    ADD_RULE: {
      actions: assign({
        rules: ({ context, event }) =>
          event.type === 'ADD_RULE' ? [...context.rules, event.rule] : context.rules,
      }),
    },
    UPDATE_RULE: {
      actions: assign({
        rules: ({ context, event }) => {
          if (event.type !== 'UPDATE_RULE') return context.rules
          return context.rules.map((r) =>
            r.id === event.id ? { ...r, ...event.updates, updatedAt: Date.now() } : r
          )
        },
      }),
    },
    DELETE_RULE: {
      actions: assign({
        rules: ({ context, event }) => {
          if (event.type !== 'DELETE_RULE') return context.rules
          return context.rules.filter((r) => r.id !== event.id)
        },
      }),
    },
    SET_QUEUES: {
      actions: assign({
        queues: ({ event }) => (event.type === 'SET_QUEUES' ? event.queues : []),
      }),
    },
    ADD_QUEUE: {
      actions: assign({
        queues: ({ context, event }) =>
          event.type === 'ADD_QUEUE' ? [...context.queues, event.queue] : context.queues,
      }),
    },
    UPDATE_QUEUE: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'UPDATE_QUEUE') return context.queues
          return context.queues.map((q) =>
            q.id === event.id ? { ...q, ...event.updates, updatedAt: Date.now() } : q
          )
        },
      }),
    },
    DELETE_QUEUE: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'DELETE_QUEUE') return context.queues
          return context.queues.filter((q) => q.id !== event.id)
        },
      }),
    },
    INCREMENT_QUEUE_LOAD: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'INCREMENT_QUEUE_LOAD') return context.queues
          return context.queues.map((q) =>
            q.id === event.id ? { ...q, currentLoad: q.currentLoad + 1 } : q
          )
        },
      }),
    },
    DECREMENT_QUEUE_LOAD: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'DECREMENT_QUEUE_LOAD') return context.queues
          return context.queues.map((q) =>
            q.id === event.id ? { ...q, currentLoad: Math.max(0, q.currentLoad - 1) } : q
          )
        },
      }),
    },
    SET_AGENT_SKILLS: {
      actions: assign({
        agentSkills: ({ event }) => (event.type === 'SET_AGENT_SKILLS' ? event.skills : []),
      }),
    },
    UPDATE_AGENT_SKILLS: {
      actions: assign({
        agentSkills: ({ context, event }) => {
          if (event.type !== 'UPDATE_AGENT_SKILLS') return context.agentSkills
          return context.agentSkills.map((s) =>
            s.agentId === event.agentId ? { ...s, ...event.skills } : s
          )
        },
      }),
    },
    CLEAR: {
      actions: assign({
        rules: [],
        queues: [],
        agentSkills: [],
      }),
    },
  },
})
